// Phase 3c — HO Corporate Finance admin console pages (single-file bundle).
// Consumes /api/admin/subscription-ledger, /ho-expenses, /ai-profitability,
// /pnl, /exports/gstr1. Reuses existing admin styling (Tailwind + slate/pink
// palette from AppShell) — no rebrand.
import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/adminApi";
import { useToast } from "../components/Toast";

// ---------------- helpers ---------------------------------------------------
function inr(n) {
  const v = Number(n) || 0;
  return "₹" + v.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}
function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function MonthPicker({ value, onChange, testid = "month-picker" }) {
  return (
    <input
      type="month"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="border border-[#7C5CFF]/[0.15] rounded-lg px-3 py-1.5 text-sm bg-white"
    />
  );
}
function PlanChip({ plan }) {
  const map = {
    free: "bg-slate-100 text-slate-700",
    trial: "bg-violet-100 text-violet-700",
    standard: "bg-blue-100 text-blue-700",
    pro: "bg-teal-100 text-teal-700",
    premium: "bg-pink-100 text-pink-700",
  };
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${map[plan] || map.free}`}>{plan}</span>;
}
function StatusChip({ status }) {
  const map = {
    trial: "bg-violet-100 text-violet-700",
    paid: "bg-green-100 text-green-700",
    pending: "bg-amber-100 text-amber-700",
    overdue: "bg-red-100 text-red-700",
    free: "bg-slate-100 text-[#5B617A]",
  };
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${map[status] || map.free}`}>{status}</span>;
}

// ============================================================================
// Subscription Ledger
// ============================================================================
export function SubscriptionLedgerPage() {
  const toast = useToast();
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  // Platform settings (Slice A2)
  const [commissionPct, setCommissionPct] = useState("");
  const [savingCommission, setSavingCommission] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/subscription-ledger?month=${month}`); setData(r.data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, [month]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/platform-settings")
      .then((r) => setCommissionPct(String((r.data.gateway_commission_pct * 100).toFixed(2))))
      .catch(() => {});
  }, []);

  const totals = data?.totals || {};
  const allRows = data?.tenants || [];
  const rows = allRows.filter((t) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (t.name || "").toLowerCase().includes(q) ||
      (t.owner_email || "").toLowerCase().includes(q) ||
      (t.owner_phone || "").toLowerCase().includes(q)
    );
  });

  const saveCommission = async () => {
    const pct = Number(commissionPct);
    if (isNaN(pct) || pct < 0 || pct > 50) {
      toast.error("Enter a % between 0 and 50");
      return;
    }
    setSavingCommission(true);
    try {
      await api.patch("/platform-settings", { gateway_commission_pct: pct / 100 });
      toast.success(`Gateway commission set to ${pct}%`);
      await load();
    } catch (e) {
      toast.error("Failed to update commission");
    } finally {
      setSavingCommission(false);
    }
  };

  const downloadCsv = async () => {
    const r = await api.get(`/exports/gstr1?month=${month}`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a");
    a.href = url; a.download = `gstr1-${month}.csv`; document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="p-8 glass-animate" data-testid="ledger-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0B0F1A]">Subscription Ledger</h1>
          <p className="text-sm text-[#5B617A] mt-0.5">All tenants · payments · commission · net revenue</p>
        </div>
        <div className="flex items-center gap-3">
          <MonthPicker value={month} onChange={setMonth} testid="ledger-month" />
          <button
            data-testid="export-gstr1-btn"
            onClick={downloadCsv}
            className="px-4 py-1.5 rounded-xl btn-primary-glass text-sm"
          >Export GSTR-1</button>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-3 mb-6 glass-animate" data-testid="ledger-totals">
        <TotalCard label="Gross" value={inr(totals.gross_inr)} />
        <TotalCard label="Commission" value={inr(totals.commission_inr)} />
        <TotalCard label="Net Revenue" value={inr(totals.net_inr)} highlight />
        <TotalCard label="Trials" value={String(totals.trial_count || 0)} />
        <TotalCard label="Paid" value={String(totals.paid_count || 0)} />
      </div>

      {/* Slice A2 — Platform Settings inline (Gateway Commission) + A4 search */}
      <div className="flex items-center gap-3 mb-4">
        <input
          type="text"
          data-testid="ledger-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, or phone…"
          className="flex-1 border border-[#7C5CFF]/[0.15] rounded-lg px-3 py-1.5 text-sm bg-white"
        />
        <div className="flex items-center gap-2 bg-white/85 backdrop-blur-xl border border-[#7C5CFF]/[0.12] rounded-lg px-3 py-1.5">
          <span className="text-xs text-[#5B617A] font-semibold">Gateway commission</span>
          <input
            type="number"
            data-testid="commission-input"
            value={commissionPct}
            onChange={(e) => setCommissionPct(e.target.value)}
            step="0.1"
            min="0"
            max="50"
            className="w-16 text-sm font-semibold text-[#0B0F1A] focus:outline-none border-b border-[#7C5CFF]/[0.12]"
          />
          <span className="text-xs text-[#5B617A]">%</span>
          <button
            data-testid="save-commission-btn"
            onClick={saveCommission}
            disabled={savingCommission}
            className={`ml-1 px-2.5 py-1 rounded-md text-xs font-semibold ${savingCommission ? "bg-slate-200 text-[#5B617A]" : "bg-[#7C5CFF] text-white hover:bg-[#5A3FD9]"}`}
          >
            {savingCommission ? "…" : "Save"}
          </button>
        </div>
        <span className="text-xs text-[#5B617A]/60" data-testid="ledger-count">
          {rows.length} of {allRows.length}
        </span>
      </div>

      <div className="glass-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/40 text-[#5B617A] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Tenant</th>
              <th className="text-left px-4 py-3">Owner Email</th>
              <th className="text-left px-4 py-3">Plan</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Trial</th>
              <th className="text-right px-4 py-3">Amount Paid</th>
              <th className="text-right px-4 py-3">Commission</th>
              <th className="text-right px-4 py-3">Net</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" className="text-center py-8 text-[#5B617A]">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="8" className="text-center py-8 text-[#5B617A]">No tenants this month.</td></tr>
            ) : rows.map((t) => (
              <tr key={t.tenant_id} className="border-t border-[#7C5CFF]/[0.04] glass-row-hover">
                <td className="px-4 py-3 font-semibold text-[#0B0F1A]">{t.name}</td>
                <td className="px-4 py-3 text-[#5B617A]">{t.owner_email || "—"}</td>
                <td className="px-4 py-3"><PlanChip plan={t.plan} /></td>
                <td className="px-4 py-3"><StatusChip status={t.payment_status} /></td>
                <td className="px-4 py-3 text-[#5B617A]">
                  {t.trial_active ? `${t.trial_days_left}d left` : "—"}
                </td>
                <td className="px-4 py-3 text-right font-semibold">{inr(t.amount_paid_inr)}</td>
                <td className="px-4 py-3 text-right text-[#5B617A]">{inr(t.gateway_commission_inr)}</td>
                <td className="px-4 py-3 text-right font-bold text-green-700">{inr(t.net_revenue_inr)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TotalCard({ label, value, highlight }) {
  return (
    <div className={`rounded-[20px] border p-4 ${highlight ? "bg-green-50 border-green-200" : "glass-card"}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-[#5B617A]">{label}</div>
      <div className={`text-xl font-extrabold mt-1 ${highlight ? "text-green-700" : "text-[#0B0F1A]"}`}>{value}</div>
    </div>
  );
}

// ============================================================================
// HO Expenses
// ============================================================================
const CATS = ["salary", "rent", "utilities", "marketing", "software", "other"];

export function HOExpensesPage() {
  const toast = useToast();
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(null);   // null | "new" | expense obj
  const load = useCallback(async () => {
    const r = await api.get(`/ho-expenses?month=${month}`); setData(r.data);
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const save = async (payload, id) => {
    try {
      if (id) await api.patch(`/ho-expenses/${id}`, payload);
      else await api.post(`/ho-expenses`, payload);
      toast.success(id ? "Expense updated" : "Expense added");
      setModal(null); await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await db_delete(id); toast.success("Expense deleted"); await load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };
  const db_delete = (id) => api.delete(`/ho-expenses/${id}`);

  const items = data?.items || [];
  const byCat = data?.by_category || {};

  return (
    <div className="p-8 glass-animate" data-testid="ho-expenses-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0B0F1A]">HO Expenses</h1>
          <p className="text-sm text-[#5B617A] mt-0.5">Head-office costs · salaries · rent · marketing</p>
        </div>
        <div className="flex items-center gap-3">
          <MonthPicker value={month} onChange={setMonth} testid="expenses-month" />
          <button data-testid="add-expense-btn" onClick={() => setModal("new")}
            className="px-4 py-1.5 rounded-lg bg-[#7C5CFF] text-white text-sm font-semibold hover:bg-[#5A3FD9]">
            + Add Expense
          </button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3 mb-6">
        <TotalCard label="Total this month" value={inr(data?.total_inr)} highlight />
        {CATS.slice(0, 3).map((c) => (
          <TotalCard key={c} label={c[0].toUpperCase() + c.slice(1)} value={inr(byCat[c] || 0)} />
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-[#7C5CFF]/[0.12] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[#5B617A] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-left px-4 py-3">Description</th>
              <th className="text-right px-4 py-3">Amount</th>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Recurring</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan="6" className="text-center py-8 text-[#5B617A]/60">No expenses this month.</td></tr>
            ) : items.map((e) => (
              <tr key={e.id} className="border-t border-[#7C5CFF]/[0.06] hover:bg-slate-50">
                <td className="px-4 py-3"><PlanChip plan={e.category} /></td>
                <td className="px-4 py-3 text-[#0B0F1A]">{e.description}</td>
                <td className="px-4 py-3 text-right font-semibold">{inr(e.amount_inr)}</td>
                <td className="px-4 py-3 text-[#5B617A]">{e.expense_date}</td>
                <td className="px-4 py-3 text-[#5B617A]">
                  {e.recurring ? <span className="text-green-700">{e.recurring_frequency}</span> : "—"}
                </td>
                <td className="px-4 py-3 text-right text-xs">
                  <button onClick={() => setModal(e)} className="text-blue-600 hover:underline mr-3">Edit</button>
                  <button onClick={() => del(e.id)} className="text-red-500 hover:underline">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal ? <ExpenseModal init={modal === "new" ? null : modal} onSave={save} onClose={() => setModal(null)} /> : null}
    </div>
  );
}

function ExpenseModal({ init, onSave, onClose }) {
  const [form, setForm] = useState(init || {
    category: "salary", description: "", amount_inr: "",
    expense_date: new Date().toISOString().slice(0, 10),
    recurring: false, recurring_frequency: null,
  });
  const submit = (e) => {
    e.preventDefault();
    const payload = { ...form, amount_inr: Number(form.amount_inr) || 0 };
    if (!payload.recurring) payload.recurring_frequency = null;
    onSave(payload, init?.id);
  };
  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50" data-testid="expense-modal">
      <form onSubmit={submit} className="glass-modal p-6 w-[440px] space-y-3">
        <h3 className="text-lg font-bold text-[#0B0F1A]">{init ? "Edit" : "Add"} Expense</h3>
        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
          className="w-full border border-[#7C5CFF]/[0.12] rounded-xl px-3 py-2 text-sm bg-white/60">
          {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input placeholder="Description" value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="w-full border border-[#7C5CFF]/[0.12] rounded-xl px-3 py-2 text-sm bg-white/60" required />
        <input type="number" placeholder="Amount ₹" value={form.amount_inr}
          onChange={(e) => setForm({ ...form, amount_inr: e.target.value })}
          className="w-full border border-[#7C5CFF]/[0.12] rounded-xl px-3 py-2 text-sm bg-white/60" required />
        <input type="date" value={form.expense_date}
          onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
          className="w-full border border-[#7C5CFF]/[0.12] rounded-xl px-3 py-2 text-sm bg-white/60" required />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.recurring}
            onChange={(e) => setForm({ ...form, recurring: e.target.checked })} />
          Recurring
        </label>
        {form.recurring ? (
          <select value={form.recurring_frequency || "monthly"}
            onChange={(e) => setForm({ ...form, recurring_frequency: e.target.value })}
            className="w-full border border-[#7C5CFF]/[0.12] rounded-xl px-3 py-2 text-sm bg-white/60">
            <option value="monthly">monthly</option>
            <option value="quarterly">quarterly</option>
            <option value="yearly">yearly</option>
          </select>
        ) : null}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-1.5 rounded-xl border border-[#7C5CFF]/[0.12]">Cancel</button>
          <button type="submit" className="px-4 py-1.5 rounded-xl btn-primary-glass text-sm">Save</button>
        </div>
      </form>
    </div>
  );
}

// ============================================================================
// AI Profitability
// ============================================================================
export function AiProfitabilityPage() {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/ai-profitability?month=${month}`).then((r) => setData(r.data)).catch(() => {});
  }, [month]);
  if (!data) return <div className="p-8 text-[#5B617A]/60">Loading…</div>;
  const positive = data.margin_inr >= 0;
  return (
    <div className="p-8 glass-animate" data-testid="ai-profitability-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0B0F1A]">AI Profitability</h1>
          <p className="text-sm text-[#5B617A] mt-0.5">Revenue vs LLM infra cost</p>
        </div>
        <MonthPicker value={month} onChange={setMonth} testid="ai-month" />
      </div>

      <div className={`rounded-2xl p-6 mb-4 ${positive ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
        <div className="text-xs uppercase tracking-wider font-semibold text-[#5B617A]">Net Margin</div>
        <div className={`text-4xl font-extrabold mt-2 ${positive ? "text-green-700" : "text-red-700"}`}>
          {inr(data.margin_inr)} <span className="text-lg font-bold">({data.margin_pct}%)</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-green-50 border border-green-200 rounded-2xl p-5">
          <div className="text-xs uppercase font-semibold text-[#5B617A]">Subscription Revenue</div>
          <div className="text-2xl font-extrabold mt-1 text-green-700">{inr(data.subscription_revenue_inr)}</div>
          <div className="text-xs text-[#5B617A] mt-1">− {inr(data.gateway_commission_inr)} gateway = {inr(data.net_revenue_inr)} net</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
          <div className="text-xs uppercase font-semibold text-[#5B617A]">AI Infrastructure Costs</div>
          <div className="text-2xl font-extrabold mt-1 text-amber-700">{inr(data.ai_costs_inr)}</div>
          {!data.instrumentation_ready ? (
            <div className="text-[11px] text-[#5B617A] mt-1">{data.note}</div>
          ) : null}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#7C5CFF]/[0.12] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#7C5CFF]/[0.06] font-bold text-[#0B0F1A]">Top 10 tenants by AI cost</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[#5B617A] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Tenant</th>
              <th className="text-right px-4 py-3">Cost ₹</th>
            </tr>
          </thead>
          <tbody>
            {(data.ai_costs_by_tenant || []).length === 0 ? (
              <tr><td colSpan="2" className="text-center py-8 text-[#5B617A]/60">No AI usage recorded this month.</td></tr>
            ) : data.ai_costs_by_tenant.map((t) => (
              <tr key={t.tenant_id} className="border-t border-[#7C5CFF]/[0.06]">
                <td className="px-4 py-3">{t.name}</td>
                <td className="px-4 py-3 text-right font-semibold">{inr(t.cost_inr)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// P&L Statement
// ============================================================================
export function PnLPage() {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/pnl?month=${month}`).then((r) => setData(r.data)).catch(() => {});
  }, [month]);
  if (!data) return <div className="p-8 text-[#5B617A]/60">Loading…</div>;
  const rev = data.revenue;
  const cost = data.costs;
  const positive = data.net_profit >= 0;

  return (
    <div className="p-8 glass-animate" data-testid="pnl-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0B0F1A]">P&amp;L Statement</h1>
          <p className="text-sm text-[#5B617A] mt-0.5">Consolidated · {data.month}</p>
        </div>
        <MonthPicker value={month} onChange={setMonth} testid="pnl-month" />
      </div>

      <div className="bg-white rounded-2xl border border-[#7C5CFF]/[0.12] p-8 max-w-2xl mx-auto">
        <Section title="Revenue" color="green">
          <Row label="Subscriptions" value={inr(rev.subscriptions)} />
          <Row label="Other" value={inr(rev.other)} />
          <Row label="Total Revenue" value={inr(rev.total)} bold total />
        </Section>

        <Section title="Costs" color="red">
          <Row label="Gateway Commission" value={inr(cost.gateway_commission)} />
          <Row label="AI Infrastructure" value={inr(cost.ai_infrastructure)} />
          <div className="pl-4 mt-2 border-l-2 border-[#7C5CFF]/[0.06] space-y-1">
            <div className="text-xs uppercase font-semibold text-[#5B617A]">HO Expenses</div>
            {Object.entries(cost.ho_expenses_by_category || {}).length === 0 ? (
              <div className="text-xs text-[#5B617A]/60 italic">None recorded.</div>
            ) : Object.entries(cost.ho_expenses_by_category).map(([k, v]) => (
              <Row key={k} label={k[0].toUpperCase() + k.slice(1)} value={inr(v)} small />
            ))}
            <Row label="HO Expenses Total" value={inr(cost.ho_expenses_total)} bold />
          </div>
          <Row label="Total Costs" value={inr(cost.total_costs)} bold total />
        </Section>

        <div className={`mt-6 p-5 rounded-xl ${positive ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
          <div className="text-xs uppercase font-semibold text-[#5B617A]">Net Profit</div>
          <div className={`text-3xl font-extrabold mt-1 ${positive ? "text-green-700" : "text-red-700"}`}>
            {inr(data.net_profit)} <span className="text-base font-bold">({data.net_margin_pct}%)</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, color, children }) {
  const c = color === "green" ? "text-green-700" : color === "red" ? "text-red-700" : "text-[#0B0F1A]";
  return (
    <div className="mb-5">
      <h3 className={`text-sm font-bold uppercase tracking-wider mb-2 ${c}`}>{title}</h3>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function Row({ label, value, bold, total, small }) {
  return (
    <div className={`flex items-center justify-between ${small ? "text-xs" : "text-sm"} ${total ? "border-t border-[#7C5CFF]/[0.08] pt-2 mt-1" : ""}`}>
      <span className={bold ? "font-bold text-[#0B0F1A]" : "text-[#5B617A]"}>{label}</span>
      <span className={bold ? "font-bold text-[#0B0F1A]" : "text-[#0B0F1A]"}>{value}</span>
    </div>
  );
}
